import axios, { AxiosError } from 'axios'
import fileDownload from 'js-file-download'
import { ApiError, ApiRequest } from './ApiTypes'

const { CancelToken } = axios
export const serverUrl: string = process.env.REACT_APP_API_URL || 'http://nobaseurl.example.com/'

function getString(obj: { [key: string]: string | null | number } | null, key: string): string | null {
  if (obj && key in obj) {
    const x = obj[key]
    if (typeof x === 'string') return x
  }
  return null
}

function transformError(error: AxiosError): ApiError {
  if (!error) return null
  if (!error.response) return { code: `E_CONNECT_FAIL`, httpStatus: 0, message: error.message }
  if (!error.response.data) return { code: error.response.statusText, httpStatus: error.response.status, message: error.message }
  return {
    code: getString(error.response.data, 'code') || error.response.statusText,
    httpStatus: error.response.status,
    message: getString(error.response.data, 'error') || 'Something went quite wrong.',
  }
}

interface ApiCancelFn {
  (): void
}

const tokenKey = 'authToken'
const isTokenSharedKey = 'isTokenShared'

export const getToken = (): string | null => {
  const token = localStorage.getItem(tokenKey)
  if (token && token.length > 1) return token
  return null
}

export const getIsTokenShared = (): boolean => {
  const isTokenShared = localStorage.getItem(isTokenSharedKey)
  return isTokenShared === 'true'
}

export const setIsTokenShared = (value: boolean): void => {
  localStorage.setItem(isTokenSharedKey, value.toString())
}

// Delete the flag entirely so the next external navigation re-shares a fresh
// token. Used when the new frontend bounces the user back with `?unauthorized`.
export const removeIsTokenShared = (): void => {
  localStorage.removeItem(isTokenSharedKey)
}

export const setToken = (token: string | undefined): void => {
  if (token && token !== 'undefined') {
    localStorage.setItem(tokenKey, token)
    setIsTokenShared(false)
  }
}

export const unsetToken = (): void => {
  const token = localStorage.getItem(tokenKey)
  if (token) {
    localStorage.removeItem(tokenKey)
    setIsTokenShared(false)
  }
}

function server<R, P>(request: ApiRequest<R, P>): ApiCancelFn {
  const source = CancelToken.source()

  const { path, method, timeout, variablesJson, onSuccess, onFail } = request
  const token = getToken()
  const headers = request.headers || {}
  if (token) {
    headers['Authorization'] = token
  }

  const params = variablesJson ? JSON.parse(variablesJson) : undefined
  axios({
    url: path,
    method,
    baseURL: serverUrl,
    timeout: timeout || 40000,
    params,
    data: request.data,
    headers: headers || {},
    responseType: 'json',
    cancelToken: source.token,
  })
    .then((response) => {
      onSuccess(response.data as R, response.headers.authorization)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) {
        onFail(transformError(error))
      }
    })

  return () => {
    source.cancel()
  }
}

export const authorizeDownloadAttachments = (url: string, folderName: string, entitySlug: string) => {
  const token = getToken()
  const filename = `${entitySlug}_${folderName}.zip`
  axios
    .get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      fileDownload(res.data, filename)
    })
}
export default server
